import { useEffect, useMemo, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { Badge, CustomInput, Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from "reactstrap";
import ApplicationMenu from "../../components/common/ChatMenu";
import { firestore } from "../../helpers/Firebase";
import { accessTypes, compareContacts, FormatTimestamp, getFirstCharacters } from "../../helpers/Utils";
import { getContacts, setChatsFilter, setUserFromRequests } from "../../redux/chat/actions";
import LabelsManagement from "../../views/app/chat/components/LabelsManagement";

const ChatApplicationMenu = ({ onLoadMoreContacts, hasMoreChats, handleSearchContact, searchKey, navigateToUser, mobileFullscreen, selectedUserId }) => {
  const { allContacts, chatsFilter } = useSelector(state => state.chatApp)
  const [data, setData] = useState(allContacts);
  const [labels, setLabels] = useState([]);
  const [showLabelsManagement, setShowLabelsManagement] = useState(false);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const toggle = () => setDropdownOpen(prevState => !prevState);

  const { user } = useSelector(state => state.authUser)

  const dispatch = useDispatch()

  // Calculate active filter count
  const activeFilterCount = useMemo(() => {
    let count = 0
    if (chatsFilter?.unread) count++
    if (chatsFilter?.labelIds && chatsFilter.labelIds.length > 0) count += chatsFilter.labelIds.length
    return count
  }, [chatsFilter])

  // Fetch labels for admin users
  useEffect(() => {
    if (!accessTypes.includes(user?.accessType)) return;

    const unsubscribe = firestore()
      .collection('chatLabels')
      .where('active', '==', true)
      .orderBy('created', 'desc')
      .onSnapshot(snap => {
        const labelsData = snap?.docs?.map(doc => ({
          ...doc.data(),
          id: doc.id
        }));
        setLabels(labelsData);
      });

    return () => unsubscribe();
  }, [user]);

  const handleContactClick = (user) => {
    firestore().collection('chats').doc(user.id).update({ newCount: 0 })

    const updatedUser = { ...user, newCount: 0 };

    // Update the user in allContacts
    const updatedContacts = allContacts.map(contact =>
      contact.id === user.id ? updatedUser : contact
    );
    setData(updatedContacts);
    dispatch(getContacts(updatedContacts?.sort(compareContacts)))

    // Navigate to user's chat using URL param
    if (navigateToUser) {
      navigateToUser(user.id);
    }

    dispatch(setUserFromRequests(undefined));
  }
  useEffect(() => {
    let filteredData = allContacts
    if (searchKey !== "") {
      filteredData = filteredData?.filter(c => (c.name)?.toLowerCase()?.includes(searchKey?.toLowerCase()) || (c.wa_id)?.toLowerCase()?.includes(searchKey?.toLowerCase()))
    }
    // Note: Label filtering is now done at Firestore query level in chat.js
    // Filter by unread if selected
    if (chatsFilter?.unread) {
      filteredData = filteredData?.filter(c => c.newCount > 0)
    }
    setData(filteredData?.sort(compareContacts));
  }, [allContacts, searchKey, chatsFilter]);

  return (
    <ApplicationMenu mobileFullscreen={mobileFullscreen}>
      <div className="pt-4 pr-3 pl-3 pb-0">
        <div className="form-group d-flex align-items-center">
          <input
            type="text"
            className="form-control rounded mr-1"
            placeholder="Search by name or phone number"
            // value={searchKey}
            onChange={e => handleSearchContact(e.target.value)}
          />
          <Dropdown isOpen={dropdownOpen} toggle={toggle} >

            <DropdownToggle size='sm' color="primary" className='p-1' style={{ position: 'relative' }}>
              <i className="iconsminds-filter-2" style={{ fontSize: '18px' }} />
              {activeFilterCount > 0 && (
                <Badge
                  color="danger"
                  pill
                  style={{
                    position: 'absolute',
                    top: '-8px',
                    right: '-8px',
                    minWidth: '18px',
                    height: '18px',
                    fontSize: '10px',
                    padding: '0 5px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 600,
                    // border: '2px solid white',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                  }}
                >
                  {activeFilterCount}
                </Badge>
              )}
            </DropdownToggle>
            <DropdownMenu left size='xl' style={{ minWidth: '240px', padding: '0.5rem 0' }}>
              <DropdownItem header style={{
                padding: '0.75rem 1rem',
                fontWeight: 600,
                fontSize: '0.875rem',
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Filters
              </DropdownItem>
              <DropdownItem divider style={{ margin: '0.5rem 0' }} />

              <CustomInput
                className="custom-checkbox mx-3"
                type="checkbox"
                id="checkAll"
                checked={chatsFilter?.unread ? true : false}
                onChange={() => {
                  let cloned = JSON.parse(JSON.stringify(chatsFilter || {}))
                  if (cloned.unread) {
                    delete cloned.unread
                  } else {
                    cloned.unread = true
                  }
                  dispatch(setChatsFilter(cloned))
                }}
                label={
                  <div style={{
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    color: '#374151',
                    fontWeight: 400
                  }}>
                    Unread Chats
                  </div>
                }
              />
              {accessTypes.includes(user?.accessType) && labels.length > 0 && (
                <>
                                  <DropdownItem divider style={{ margin: '0.5rem 0' }} />

                  <DropdownItem header style={{
                    padding: '0.75rem 1rem',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Filter by Label
                  </DropdownItem>
                  <Select
                    isMulti
                    isClearable
                    placeholder="Select labels..."
                    options={labels.map(label => ({
                      value: label.id,
                      label: label.name,
                      color: label.color
                    }))}
                    value={labels
                      .filter(label => chatsFilter?.labelIds?.includes(label.id))
                      .map(label => ({
                        value: label.id,
                        label: label.name,
                        color: label.color
                      }))}
                    onChange={(selectedOptions) => {
                      let cloned = JSON.parse(JSON.stringify(chatsFilter || {}))
                      if (selectedOptions && selectedOptions.length > 0) {
                        cloned.labelIds = selectedOptions.map(opt => opt.value)
                      } else {
                        delete cloned.labelIds
                      }
                      dispatch(setChatsFilter(cloned))
                    }}
                    className="basic-multi-select"
                    classNamePrefix="select"
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: '38px',
                        margin: '0.5rem 1rem',
                        borderColor: '#dee2e6',
                        '&:hover': {
                          borderColor: '#d1d5db'
                        }
                      }),
                      menu: (base) => ({
                        ...base,
                        zIndex: 9999,
                        margin: '0.5rem 1rem',
                      })
                    }}
                    formatOptionLabel={({ label, color }) => (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div
                          style={{
                            width: '12px',
                            height: '12px',
                            borderRadius: '3px',
                            backgroundColor: color || '#6c757d',
                            border: '1px solid rgba(0,0,0,0.1)',
                            flexShrink: 0
                          }}
                        />
                        <span>{label}</span>
                      </div>
                    )}
                  />
                </>
              )}
              {accessTypes.includes(user?.accessType) && (
                <>
                  <DropdownItem divider style={{ margin: '0.5rem 0' }} />
                  <DropdownItem
                    onClick={() => setShowLabelsManagement(true)}
                    style={{
                      margin: '0.75rem 1rem 0.5rem 1rem',
                      borderRadius: '0.5rem',
                      border: '1px solid #dee2e6',
                      padding: '0.5rem 1rem',
                      width: 'calc(100% - 2rem)',
                      backgroundColor: '#f8f9fa',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      color: '#374151',
                      fontWeight: 400,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <i className="simple-icon-tag" style={{ fontSize: '14px' }} />
                    <span>Manage Labels</span>
                  </DropdownItem>
                </>
              )}
            </DropdownMenu>
          </Dropdown>
        </div>
      </div>
      {showLabelsManagement && (
        <LabelsManagement onDismiss={() => setShowLabelsManagement(false)} />
      )}
      <div
        id="scrollableChats"
        style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
          minHeight: 0
        }}
      >
        <InfiniteScroll
          // className="pt-2 pb-2"
          dataLength={data?.length || 0}
          next={onLoadMoreContacts}
          style={{ backgroundColor: 'white' }}
          hasMore={hasMoreChats}
          loader={
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '20px 0',
              width: '100%'
            }}>
              <div style={{
                display: 'inline-block',
                width: '30px',
                height: '30px',
                border: '2px solid rgba(145, 145, 145, 0.2)',
                borderRadius: '50%',
                borderTopColor: '#145388',
                animation: 'spin 1s ease-in-out infinite',
                WebkitAnimation: 'spin 1s ease-in-out infinite'
              }} />
            </div>
          }
          scrollableTarget="scrollableChats"
          endMessage={
            <p style={{ textAlign: "center", padding: '20px 0' }}>
              <b>No more chats</b>
            </p>
          }
        >
          {
            data?.map((item, index) => {
              const isActive = selectedUserId === item.id;
              return (
                <div
                  key={index}
                  className={`chat-item-container ${isActive ? 'active' : ''}`}
                  onClick={() => handleContactClick(item)}
                >
                  <div className="chat-item-avatar">
                    {getFirstCharacters(item?.name || 'Chillapp Team')}
                  </div>
                  <div className="chat-item-content">
                    <div className="chat-item-header">
                      <h6 className="chat-item-name">{item?.name}</h6>
                      <span className="chat-item-time">{FormatTimestamp(item?.updated)}</span>
                    </div>
                    <div className="chat-item-footer">
                      {accessTypes.includes(user?.accessType) && (
                        <span className="chat-item-id">{item?.wa_id}</span>
                      )}
                      <div className="chat-item-badges">
                        {item.exp && (
                          <span className="chat-item-expired">Expired</span>
                        )}
                        {item.newCount > 0 && (
                          <Badge className="chat-item-unread" color="danger">
                            {item.newCount}
                          </Badge>
                        )}
                      </div>
                    </div>
                    {accessTypes.includes(user?.accessType) && item?.labels && item.labels.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
                        {item.labels.map(labelId => {
                          const label = labels.find(l => l.id === labelId);
                          if (!label) return null;
                          const labelColor = label.color || '#6c757d';
                          return (
                            <Badge
                              key={labelId}
                              className="chat-label-badge"
                              style={{
                                '--label-bg-color': labelColor,
                                backgroundColor: labelColor,
                                color: '#fff',
                                fontSize: '10px',
                                padding: '2px 6px',
                                borderRadius: '10px',
                                border: 'none'
                              }}
                            >
                              {label.name}
                            </Badge>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

        </InfiniteScroll>
      </div>
    </ApplicationMenu>
  )
};

export default ChatApplicationMenu;